import { unwrap, type UserOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, toast } from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { queryKeys } from "../../../../lib/query-keys";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { BackupCodesDialog, DisableTwoFactorDialog, RegenerateCodesDialog } from "./two-factor-dialogs";
import { TwoFactorSetup, type SetupState } from "./two-factor-setup";

export { groupKey } from "./two-factor-setup";

type Dialogs = "disable" | "regenerate" | null;

/** Two-factor card: status, (re)enrol a device, backup codes, disable. */
export function TwoFactorCard({ user }: { user: UserOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const message = useErrorMessage();
  const readOnly = usePermissions().readOnly;
  const [setup, setSetup] = useState<SetupState | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [dialog, setDialog] = useState<Dialogs>(null);
  const latestRequest = useRef(0);
  const enabled = user.two_factor_enabled;
  const refreshMe = () => queryClient.invalidateQueries({ queryKey: queryKeys.me });
  const closeSetup = () => {
    latestRequest.current += 1;
    setSetup(null);
  };
  const requestSetup = async () => {
    latestRequest.current += 1;
    const request = latestRequest.current;
    setSetup({ status: "loading" });
    try {
      const result = await unwrap(api.POST("/v1/me/2fa/setup"));
      if (request === latestRequest.current) setSetup({ status: "ready", secret: result.secret, uri: result.otpauth_uri });
    } catch (caught) {
      if (request === latestRequest.current) setSetup({ status: "failed", message: message(caught) });
    }
  };
  const start = () => {
    if (setup) closeSetup();
    else void requestSetup();
  };
  const showCodes = (next: string[]) => {
    closeSetup();
    setDialog(null);
    setCodes(next);
    toast.success(t(enabled ? "settings.twoFactor.deviceVerified" : "settings.twoFactor.enabledToast"));
    void refreshMe();
  };
  const showRegenerated = (next: string[]) => {
    setDialog(null);
    setCodes(next);
    toast.success(t("settings.twoFactor.regenerated"));
    void refreshMe();
  };
  return (
    <section className="card" aria-labelledby="tfa-title">
      <div className="card-header">
        <div>
          <h2 id="tfa-title" className="card-title flex flex-wrap items-center gap-2">
            {t("settings.twoFactor.title")}
            {enabled ? (
              <Badge tone="success">
                <Icon name="shield-check" className="size-3" />
                {t("settings.twoFactor.on")}
              </Badge>
            ) : (
              <Badge tone="warning">
                <Icon name="alert-triangle" className="size-3" />
                {t("settings.twoFactor.off")}
              </Badge>
            )}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t(enabled ? "settings.twoFactor.onHint" : "settings.twoFactor.offHint")}</p>
        </div>
        <Button variant={enabled ? "secondary" : "primary"} size="sm" disabled={readOnly} aria-expanded={setup !== null} onClick={start}>
          <Icon name={enabled ? "refresh-cw" : "shield-check"} />
          {t(setup ? "settings.twoFactor.cancelSetup" : enabled ? "settings.twoFactor.newDevice" : "settings.twoFactor.setUp")}
        </Button>
      </div>
      {setup ? <TwoFactorSetup state={setup} onRetry={() => void requestSetup()} onVerified={showCodes} /> : null}
      {enabled ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line px-5 py-3 text-sm">
          <span className="text-fg-muted">{t("settings.twoFactor.codesLeft", { count: user.backup_codes_remaining })}</span>
          <span className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" disabled={readOnly} onClick={() => { setDialog("regenerate"); }}>
              <Icon name="download" />
              {t("settings.twoFactor.regenerate")}
            </Button>
            <Button variant="ghost" size="sm" className="text-danger" disabled={readOnly} onClick={() => { setDialog("disable"); }}>
              {t("settings.twoFactor.disable")}
            </Button>
          </span>
        </div>
      ) : null}
      {codes ? <BackupCodesDialog codes={codes} onClose={() => { setCodes(null); }} /> : null}
      {dialog === "disable" ? <DisableTwoFactorDialog onClose={() => { setDialog(null); }} onDone={refreshMe} /> : null}
      {dialog === "regenerate" ? <RegenerateCodesDialog onClose={() => { setDialog(null); }} onCodes={showRegenerated} /> : null}
    </section>
  );
}
